import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { PageContent } from 'src/app/components/table';
import { CompositionDTO } from 'src/app/models';
import { StorageService } from 'src/app/services/auth/storage.service';
import { environment } from 'src/environments/environment';

interface ResponseB4A<T> {
  result: T;
}

export interface CompositionDashboardStatusCell {
  status?: string;
  total?: number;
}

export interface CompositionDashboardStatusGroup {
  id?: number;
  status?: string;
  total?: number;
  pending?: number;
  statusList?: CompositionDashboardStatusCell[];
}

export interface CompositionDashboardRow {
  compositionId?: number;
  editionId?: number;
  compositionName?: string;
  subscriptionName?: string;
  total?: number;
  pending?: number;
  statusCounts?: { [key: string]: number };
}

export interface CompositionDashboardPage {
  totalSubscribers?: number;
  awaitingAllocation?: number;
  statusGroups?: CompositionDashboardStatusGroup[];
  compositions?: PageContent<CompositionDashboardRow>;
}

export interface PrepareCompositionItemResult {
  compositionId?: number;
  success?: boolean;
  pending?: number;
  error?: string;
}

export interface PrepareCompositionsResult {
  success?: number;
  error?: number;
  items?: PrepareCompositionItemResult[];
}

export interface EditionExportErpCount {
  count?: number;
}

export function mapDashboardRow(row: CompositionDashboardRow): CompositionDTO {
  const mapped: CompositionDTO = {
    compositionId: row.compositionId,
    compositionName: row.compositionName,
    subscriptionName: row.subscriptionName,
    total: row.total || 0,
    pending: row.pending || 0
  };
  Object.entries(row.statusCounts || {}).forEach(([field, value]) => {
    (mapped as Record<string, number>)[field] = value;
  });
  return mapped;
}

const SEPARACAO_MILLENNIUM_STATUS_ORDER = [
  'AG EXPORTACAO',
  'AG RESSUPRIMENTO',
  'AG FATURAMENTO',
  'AG EMBARQUE',
  'CANCELADO',
  'PEDIDO SUSPENSO'
];

function normalizeMillenniumStatus(status?: string): string {
  return (status ?? '')
    .replace(/\./g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

function getSeparacaoMillenniumSortIndex(status?: string): number {
  const index = SEPARACAO_MILLENNIUM_STATUS_ORDER.indexOf(
    normalizeMillenniumStatus(status)
  );
  return index === -1 ? SEPARACAO_MILLENNIUM_STATUS_ORDER.length : index;
}

function isSeparacaoStatusGroup(
  statusGroup: CompositionDashboardStatusGroup
): boolean {
  return (
    statusGroup.id === 2 ||
    normalizeMillenniumStatus(statusGroup.status).includes('SEPARACAO')
  );
}

export function sortDashboardStatusGroups(
  groups: CompositionDashboardStatusGroup[]
): CompositionDashboardStatusGroup[] {
  return groups.map((group) => {
    const statusList = [...(group.statusList || [])];
    if (!statusList.length) {
      return { ...group, statusList };
    }
    if (isSeparacaoStatusGroup(group)) {
      statusList.sort(
        (left, right) =>
          getSeparacaoMillenniumSortIndex(left.status) -
          getSeparacaoMillenniumSortIndex(right.status)
      );
    } else {
      statusList.sort((left, right) =>
        (left.status ?? '').localeCompare(right.status ?? '', 'pt-BR', {
          sensitivity: 'base'
        })
      );
    }
    return { ...group, statusList };
  });
}

export function statusGroupFields(
  groups: CompositionDashboardStatusGroup[]
): string[] {
  return groups.flatMap((group) =>
    (group.statusList || []).map((cell) => `${cell.status}_${group.id}`)
  );
}

export function sameFieldList(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((field, index) => field === right[index]);
}

@Injectable({ providedIn: 'root' })
export class EditionsDashboardClient {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/composition`;

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${StorageService.token}`
    });
  }

  findDashboardPage(
    editionId: number,
    page: number,
    pageSize: number,
    pendingOnly: boolean,
    includeAggregates = true,
    sortBy?: string,
    sortDirection?: string
  ): Observable<CompositionDashboardPage> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize))
      .set('pendingOnly', String(pendingOnly))
      .set('includeAggregates', String(includeAggregates));
    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }
    if (sortDirection) {
      params = params.set('sortDirection', sortDirection);
    }
    return this.http
      .get<ResponseB4A<CompositionDashboardPage>>(
        `${this.baseUrl}/dashboard/${editionId}/compositions`,
        { headers: this.authHeaders(), params }
      )
      .pipe(map((data) => data.result));
  }

  prepareBatch(compositionIds: number[]): Observable<PrepareCompositionsResult> {
    return this.http
      .put<ResponseB4A<PrepareCompositionsResult>>(
        `${this.baseUrl}/admin/prepare-batch`,
        { compositionIds },
        { headers: this.authHeaders() }
      )
      .pipe(map((data) => data.result));
  }

  countExportErp(editionId: number): Observable<EditionExportErpCount> {
    return this.http
      .get<ResponseB4A<EditionExportErpCount>>(
        `${this.baseUrl}/edition/${editionId}/export-erp/count`,
        { headers: this.authHeaders() }
      )
      .pipe(map((data) => data.result));
  }

  exportErp(editionId: number): Observable<string> {
    return this.http
      .post<ResponseB4A<string>>(
        `${this.baseUrl}/edition/${editionId}/export-erp`,
        {},
        { headers: this.authHeaders() }
      )
      .pipe(map((data) => data.result));
  }
}
